// P4 · Cross-outlet contrast drawer. The "framing spread made browsable": every fact
// claim, its attribution status, which outlets carry it, who contests it, filterable by
// frame polarity. Stays visibly CONSTANT across reframes (the honesty anchor in C5).

import type { Dispatch } from "react";
import type { Bundle } from "../types";
import type { UIState, Patch } from "../state";
import type { Telemetry } from "../telemetry";
import { INK, badgeFor, POL_LABEL, POL_STYLE, sectionLabel } from "../style";
import { SourceLink, findSource, type OpenSource } from "./SourceLink";

const FILTERS: [UIState["factFilter"], string][] = [["all", "All"], ["threat", "Threat"], ["human", "Human"], ["neutral", "Neutral"]];

const outletChipStyle = {
  fontSize: 10.5, fontWeight: 600, color: "rgba(55,53,47,.7)", background: "#fff",
  border: "1px solid rgba(55,53,47,.18)", borderRadius: 4, padding: "1px 7px", whiteSpace: "nowrap",
} as const;

export function ContrastDrawer({ b, s, set, tel, onOpenSource }: {
  b: Bundle; s: UIState; set: Dispatch<Patch>; tel: Telemetry; onOpenSource?: OpenSource;
}) {
  const claims = b.fact_table.claims.filter((c) => s.factFilter === "all" || c.frame_polarity === s.factFilter);
  const nOutlets = b.incident.outlets.length;

  return (
    <div>
      <div style={{ ...sectionLabel, marginBottom: 3 }}>The facts, across outlets</div>
      <p style={{ fontSize: 11.5, color: "rgba(55,53,47,.5)", lineHeight: 1.45, margin: "0 0 11px" }}>
        Every claim, who carries it, and who disputes it. These stay fixed no matter how the story is framed.
      </p>

      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 12 }}>
        {FILTERS.map(([k, label]) => {
          const on = s.factFilter === k;
          return (
            <button key={k} onClick={() => set({ factFilter: k })}
              style={{ font: "inherit", fontSize: 11.5, fontWeight: 600, padding: "4px 10px", borderRadius: 5, cursor: "pointer", border: "1px solid " + (on ? INK : "rgba(55,53,47,.16)"), background: on ? INK : "#fff", color: on ? "#fff" : "rgba(55,53,47,.6)" }}>
              {label}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {claims.map((c) => {
          const open = !!s.openClaims[c.claim_id];
          const active = s.activeClaim === c.claim_id;
          const bd = badgeFor(c.attribution_status);
          return (
            <div key={c.claim_id} style={{ background: "#fff", border: "1px solid " + (active ? "rgba(55,53,47,.45)" : "rgba(55,53,47,.12)"), borderRadius: 7, padding: "12px 13px", transition: "border-color .12s", boxShadow: active ? "0 0 0 2px rgba(55,53,47,.07)" : "none" }}>
              <button onClick={() => {
                  const was = !!s.openClaims[c.claim_id];
                  if (!was) tel.log("contrast_claim_open", { claim_id: c.claim_id, attribution_status: c.attribution_status });
                  set({ openClaims: { ...s.openClaims, [c.claim_id]: !was }, activeClaim: was ? null : c.claim_id });
                }}
                style={{ width: "100%", textAlign: "left", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={POL_STYLE[c.frame_polarity]} title={POL_LABEL[c.frame_polarity]} />
                  <span style={{ flex: 1, fontSize: 13.5, lineHeight: 1.45, fontWeight: 600, color: INK }}>{c.text}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, flexWrap: "wrap", paddingLeft: 17 }}>
                  <span style={bd.style}>{bd.label}</span>
                  <span style={{ fontSize: 10.5, color: "rgba(55,53,47,.42)", fontWeight: 500 }}>in {c.corroborating_outlets.length}/{nOutlets} outlets</span>
                </div>
              </button>
              {open && (
                <div className="lensfade" style={{ marginTop: 11, padding: "11px 0 2px 17px", borderTop: "1px solid rgba(55,53,47,.09)", fontSize: 12, lineHeight: 1.55, color: "rgba(55,53,47,.65)" }}>
                  <div style={{ marginBottom: 6 }}><span style={{ color: "rgba(55,53,47,.42)" }}>Asserted by</span> {c.asserted_by.join(", ")}</div>
                  {c.corroborating_outlets.some((o) => findSource(b.sources, o)) && (
                    <div style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                      <span style={{ color: "rgba(55,53,47,.42)" }}>Reported by</span>
                      {c.corroborating_outlets.filter((o) => findSource(b.sources, o)).map((o) => (
                        <span key={o} style={outletChipStyle}>
                          <SourceLink outlet={o} sources={b.sources} meta={{ from: "contrast_claim", claim_id: c.claim_id }} onOpen={onOpenSource} />
                        </span>
                      ))}
                    </div>
                  )}
                  {c.contested_by.length > 0 && (
                    <div style={{ marginBottom: 6 }}><span style={{ color: "rgba(55,53,47,.42)" }}>Contested by</span>{" "}
                      {c.contested_by.map((o, i) => (
                        <span key={o + i}>{i > 0 ? ", " : ""}<SourceLink outlet={o} sources={b.sources} meta={{ from: "contrast_claim", claim_id: c.claim_id }} onOpen={onOpenSource} /></span>
                      ))}</div>
                  )}
                  <div style={{ marginBottom: 6 }}><span style={{ color: "rgba(55,53,47,.42)" }}>Frame</span> {POL_LABEL[c.frame_polarity]}</div>
                  {c.verbatim_support && (
                    <div style={{ background: "#f7f6f3", borderLeft: "2px solid rgba(55,53,47,.2)", padding: "7px 10px", borderRadius: "0 4px 4px 0", fontFamily: "'Source Sans 3',serif", fontStyle: "italic", color: "rgba(55,53,47,.6)" }}>"{c.verbatim_support}"</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 13, paddingTop: 12, borderTop: "1px solid rgba(55,53,47,.09)", fontSize: 11, color: "rgba(55,53,47,.42)", lineHeight: 1.5 }}>
        Sources present across coverage: <span style={{ color: "rgba(55,53,47,.6)", fontWeight: 600 }}>
          {b.fact_table.source_types_present.map((t, i) => (
            // items are usually source TYPES ("police", "family") which render as plain
            // text; if an item is an outlet domain with a bundled article, it links.
            <span key={t + i}>{i > 0 ? ", " : ""}<SourceLink outlet={t} sources={b.sources} label={t} meta={{ from: "contrast_claim" }} onOpen={onOpenSource} /></span>
          ))}
        </span>
      </div>
    </div>
  );
}
